import { FunctionComponent, ReactElement } from "react";

import { Chip, Paper, Typography, styled } from "@mui/material";

import { LoadingButton } from "@mui/lab";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { format } from "date-fns";

import { useGetCustomerShoppingList, useRecalculateCustomerShoppingList } from "../../hooks";
import { useMessage } from "../../sdk/hooks";
import { IShoppingListItem } from "../../sdk/types/api";
import { globalDateTimeFormat } from "../../utils/constants";

const SectionDetail = styled(Paper)`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(3)};
`;

const Header = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const SectionTitle = styled(Typography)`
    font-size: 18px;
    font-weight: 500;
    color: black;
`;

const Meta = styled(Typography)`
    font-size: 13px;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

interface IShoppingListRow extends IShoppingListItem {
    categoryName: string;
    state: "Open" | "Checked" | "Dismissed";
}

const formatQuantity = (quantity: number | null, unitLabel: string | null) =>
    quantity === null ? "As needed" : `${quantity} ${unitLabel ?? ""}`.trim();

const columns: GridColDef<IShoppingListRow>[] = [
    { field: "name", headerName: "Item", flex: 0.8 },
    {
        field: "quantity",
        headerName: "Quantity",
        flex: 0.5,
        valueGetter: ({ row }) => formatQuantity(row.quantity, row.unitLabel),
    },
    { field: "categoryName", headerName: "Category", flex: 0.5 },
    {
        field: "source",
        headerName: "Source",
        flex: 1,
        valueGetter: ({ row }) =>
            row.source === "manual"
                ? "Added manually"
                : [...new Set(row.sources.map((source) => source.recipeTitle))].join(", "),
    },
    {
        field: "state",
        headerName: "State",
        flex: 0.4,
        renderCell: ({ row }: GridRenderCellParams<IShoppingListRow>) => (
            <Chip
                size="small"
                label={row.state}
                color={row.state === "Open" ? "primary" : "default"}
                variant={row.state === "Open" ? "filled" : "outlined"}
            />
        ),
    },
];

export interface IShoppingListSectionProps {
    customerId: string;
}

/**
 * Read-only support view of the customer's shopping list, so support can
 * see what the app shows and force a recalculation.
 */
export const ShoppingListSection: FunctionComponent<IShoppingListSectionProps> = (
    props: IShoppingListSectionProps,
): ReactElement | null => {
    const { customerId } = props;
    const { showError, showSuccess } = useMessage();
    const queryKey = ["customerShoppingList", customerId];
    const listQuery = useGetCustomerShoppingList(queryKey, customerId, { enabled: Boolean(customerId) });
    const recalculateMutation = useRecalculateCustomerShoppingList(queryKey);

    const list = listQuery.data;
    if (!list) {
        return null;
    }

    const rows: IShoppingListRow[] = [
        ...list.sections.flatMap((section) =>
            section.items.map((item) => ({ ...item, categoryName: section.category.name, state: "Open" as const })),
        ),
        ...list.staples.map((item) => ({ ...item, categoryName: "Staples", state: "Open" as const })),
        ...list.checked.map((item) => ({ ...item, categoryName: "", state: "Checked" as const })),
        ...list.dismissed.map((item) => ({ ...item, categoryName: "", state: "Dismissed" as const })),
    ];

    const handleRecalculate = async () => {
        try {
            await recalculateMutation.mutateAsync({ customerId });
            showSuccess("Shopping list recalculated");
        } catch (error) {
            showError(error);
        }
    };

    return (
        <SectionDetail>
            <Header>
                <div>
                    <SectionTitle>Shopping list</SectionTitle>
                    <Meta>
                        {`${list.window.startDate} → ${list.window.endDate} · ${list.summary.mealCount} meals · ` +
                            `${list.summary.recipeCount} recipes · ${list.summary.openCount} open`}
                        {list.lastCalculatedAt &&
                            ` · calculated ${format(new Date(list.lastCalculatedAt), globalDateTimeFormat)}`}
                    </Meta>
                </div>
                <LoadingButton
                    variant="outlined"
                    size="small"
                    loading={recalculateMutation.isLoading}
                    onClick={handleRecalculate}
                >
                    Recalculate
                </LoadingButton>
            </Header>
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                disableColumnSelector
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            />
        </SectionDetail>
    );
};
